import { useEffect, useState } from "react";
import "./ViewsToolPanel.styles.scss";

// AG Grid Imports
import { CustomToolPanelProps } from "ag-grid-react";
import { GridState, FilterModel } from "ag-grid-community";

// Material UI Imports
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Util Imports
import {
  deleteUserState,
  getUserStates,
  saveUserState,
} from "../../../utils/promises";

// Package Imports
import { v4 as uuidv4 } from "uuid";
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";

const OVERVIEW = "overview";
const POSITION_SUMMARY = "position summary";

export interface ViewsToolPanelProps extends CustomToolPanelProps {
  title: string; // Title displayed at the top of the panel
  page: typeof OVERVIEW | typeof POSITION_SUMMARY; // Current page identifier
  setLoading: React.Dispatch<React.SetStateAction<boolean>>; // Loading state setter
  pushAlert: (alert: IAlertMessage) => void; // Alert message handler
}

export default ({
  title,
  page,
  setLoading,
  pushAlert,
  api,
}: ViewsToolPanelProps) => {
  // STATES - START ---------------------------------------------------------
  const OVERVIEW_GRID_ID = "046f4be1-8d5d-4687-9061-08461463e1c4";
  const POSITION_SUMMARY_GRID_ID = "8b409a05-f3d7-4968-8032-f68e9a23ac41";
  const location_origin = window.location.origin;
  const [newViewValue, setNewViewValue] = useState<string>("");
  const [userGridStates, setUserGridStates] = useState<
    {
      display_name: string;
      grid_state: GridState;
      grid_id: string;
    }[]
  >();
  const [deleteViewModal, setDeleteViewModal] = useState<{
    open: boolean;
    grid_id: string | null;
    display_name: string | null;
  }>({
    open: false,
    grid_id: null,
    display_name: null,
  });
  // STATES - END -----------------------------------------------------------

  // METHODS - START ---------------------------------------------------------
  /**
   * Fetches all saved grid states for the current user from the server.
   * Updates the userGridStates state with the retrieved data or displays
   * an error message if the fetch fails.
   *
   * @async
   * @function fetchUserGridStates
   * @param {void}
   * @returns {Promise<void>}
   *
   * @example
   * // Call the function to fetch grid states
   * await fetchUserGridStates();
   *
   * @throws {Error} Pushes an alert message if the API call fails
   */
  const fetchUserGridStates = async () => {
    try {
      // Make API call to retrieve user's saved grid states
      const get_user_grid_state_res = await getUserStates(
        location_origin,
        page === OVERVIEW ? OVERVIEW_GRID_ID : POSITION_SUMMARY_GRID_ID
      );

      // Update component state with the fetched grid states
      setUserGridStates(get_user_grid_state_res.data);
    } catch (error_res: any) {
      // Handle any errors by displaying an alert message to the user
      pushAlert({
        id: uuidv4(), // Generate unique ID for the alert
        message: error_res.message, // Use the error message from the caught error
        status: error_res?.status || 500, // Use the status code from the caught error
      });
    }
  };

  /**
   * Saves the current grid configuration state with a given name.
   * Handles the entire saving process including API communication and state updates.
   *
   * @async
   * @function saveCurrentGridState
   * @param {typeof OVERVIEW | typeof POSITION_SUMMARY} page - The current page identifier
   * @param {string} display_name - The name to save the grid state configuration as
   * @returns {Promise<void>}
   *
   * @example
   * await saveCurrentGridState(OVERVIEW, "My Custom View");
   *
   * @throws {Error} Pushes an alert message if saving fails
   */
  const saveCurrentGridState = async (
    page: typeof OVERVIEW | typeof POSITION_SUMMARY,
    display_name: string
  ) => {
    setLoading(true);

    try {
      let gridState: GridState | null = null;

      // Get current grid state if API is available
      if (api) {
        gridState = api.getState();
      }

      if (gridState) {
        // Prepare the grid state data for saving
        const gridStateData = {
          grid_id:
            page === OVERVIEW ? OVERVIEW_GRID_ID : POSITION_SUMMARY_GRID_ID,
          display_name: display_name,
          grid_state: JSON.stringify(gridState),
        };

        // Save to backend
        await saveUserState(location_origin, gridStateData);

        // Refresh the list of saved states
        fetchUserGridStates();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      setNewViewValue(""); // Clear input field
      setLoading(false); // Reset loading state
    }
  };

  /**
   * Deletes a saved grid state configuration.
   *
   * @async
   * @function removeGridState
   * @param {string} grid_id - The unique identifier of the grid state to remove
   * @param {string} display_name - The display name of the grid state to remove
   * @returns {Promise<void>}
   *
   * @example
   * await removeGridState("grid-123", "My Custom View");
   *
   * @throws {Error} Pushes an alert message if deletion fails
   */
  const removeGridState = async (grid_id: string, display_name: string) => {
    setLoading(true);

    try {
      // Validate input parameters
      if (
        grid_id &&
        grid_id.length > 0 &&
        display_name &&
        display_name.length > 0
      ) {
        // Delete the grid state
        await deleteUserState(location_origin, { grid_id, display_name });

        // Refresh the list of saved states
        fetchUserGridStates();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.message,
        status: error_res?.status || 500,
      });
    } finally {
      handleCloseDeleteViewModal();
      setLoading(false);
    }
  };

  /**
   * Loads and applies a saved grid state configuration to the current grid.
   * Handles all aspects of grid state including column visibility, ordering,
   * sizing, grouping, and sidebar state.
   *
   * @function loadGridState
   * @param {string} gridStateStr - The stringified grid state configuration to load
   * @returns {void}
   *
   * @example
   * loadGridState('{"columnState": {...}, "filterState": {...}}');
   *
   * @throws {Error} Pushes an alert message if loading fails
   */
  const loadGridState = (gridStateStr: string) => {
    setLoading(true);
    try {
      if (api) {
        // Parse the saved grid state
        const gridState: GridState = JSON.parse(gridStateStr);

        // Apply row grouping configuration
        if (gridState.rowGroup?.groupColIds) {
          api.setRowGroupColumns(gridState.rowGroup.groupColIds);
        }

        // Get all available columns
        const allColumns = api.getColumns();

        // Handle column visibility
        if (gridState.columnVisibility?.hiddenColIds) {
          if (allColumns) {
            // Determine which columns should be hidden vs visible
            const hiddenColIds = gridState.columnVisibility.hiddenColIds;
            const visibleColIds = allColumns
              .filter((col: any) => !hiddenColIds.includes(col.colId))
              .map((col: any) => col.colId);

            // Apply visibility states
            api.setColumnsVisible(hiddenColIds, false);
            api.setColumnsVisible(visibleColIds, true);
          }
        } else {
          // If no hidden columns specified, show all columns
          if (allColumns) {
            api.setColumnsVisible(allColumns, true);
          }
        }

        // Apply column ordering and sizing together if order is specified
        if (gridState.columnOrder?.orderedColIds) {
          const columnState = gridState.columnOrder.orderedColIds.map(
            (colId: any) => {
              const baseState = {
                colId,
                // Add width configuration if available
                ...(gridState.columnSizing?.columnSizingModel.find(
                  (col: any) => col.colId === colId
                )
                  ? {
                      width: gridState.columnSizing.columnSizingModel.find(
                        (col: any) => col.colId === colId
                      )!.width,
                    }
                  : {}),
                // Add sort configuration if this column is sorted
                ...(gridState.sort?.sortModel?.find(
                  (sort) => sort.colId === colId
                )
                  ? {
                      sort: gridState.sort.sortModel.find(
                        (sort) => sort.colId === colId
                      )!.sort,
                    }
                  : {}),
              };
              return baseState;
            }
          );

          // Apply combined state
          api.applyColumnState({
            state: columnState,
            applyOrder: true,
          });
        }

        // Handle sizing only if no order specified
        else if (gridState.columnSizing?.columnSizingModel) {
          const sizingState = gridState.columnSizing.columnSizingModel.map(
            (col: any) => ({
              colId: col.colId,
              width: col.width,
            })
          );
          api.applyColumnState({
            state: sizingState,
            applyOrder: false,
          });
        }

        // Apply filter states
        if (gridState.filter && gridState.filter.filterModel) {
          const filterModal: FilterModel = gridState.filter.filterModel;
          api.setFilterModel(filterModal);
        } else {
          // Clear filters if no filter model is present
          api.setFilterModel(null);
        }

        // Apply sidebar configuration
        if (gridState.sideBar) {
          api.setSideBarVisible(gridState.sideBar.visible);
          if (gridState.sideBar.openToolPanel) {
            api.openToolPanel(gridState.sideBar.openToolPanel);
          }
        }

        // Refresh grid to ensure all changes are applied
        api.onFilterChanged();
      }
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message:
          error_res?.message ||
          "Error occured while attempting to load grid state.",
        status: error_res?.status || 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteViewModal = (grid_id: string, display_name: string) => {
    setDeleteViewModal({
      open: true,
      grid_id,
      display_name,
    });
  };

  const handleCloseDeleteViewModal = () => {
    setDeleteViewModal({
      open: false,
      grid_id: null,
      display_name: null,
    });
  };

  const isDuplicateViewName = (name: string): boolean => {
    if (!userGridStates) return false;
    return userGridStates.some(
      (state) => state.display_name.toLowerCase() === name.toLowerCase()
    );
  };
  // METHODS - END -----------------------------------------------------------

  // EFFECTS - START ---------------------------------------------------------
  useEffect(() => {
    fetchUserGridStates();
  }, []);
  // EFFECTS - END -----------------------------------------------------------

  return (
    <div className="views-panel-container">
      <div className="views-panel-header pulse-mt-1 pulse-mb-2">
        <p className="pulse-typography-subtitle-1 pulse-color--background-100">
          {title}
        </p>
      </div>

      <div className="views-panel-save-input-container pulse-mb-1">
        <div className="pulse-mb-1 input-container">
          <input
            name="newViewValue"
            value={newViewValue}
            onChange={(e: any) => setNewViewValue(e.target.value)}
            placeholder="Save current view as..."
          />

          {newViewValue.length > 0 && isDuplicateViewName(newViewValue) && (
            <p className="pulse-typography--caption pulse-color--error-200">
              Please enter a unique display name
            </p>
          )}
        </div>

        <Button
          variant="contained"
          color="success"
          onClick={() => saveCurrentGridState(page, newViewValue)}
          size="small"
          disabled={
            !newViewValue ||
            newViewValue.length < 1 ||
            isDuplicateViewName(newViewValue)
          }
        >
          Save
        </Button>
      </div>

      <Divider className="views-panel-divider" />

      <div className="views-panel-grid-states-list-container pulse-mt-1">
        <div className="view-panel-grid-states-list-container-header">
          <p className="pulse-typography--body-2 pulse-mb-1">Saved Views</p>

          <Tooltip title="Click on a view to load its configuration into the table.">
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </div>
        {userGridStates && userGridStates.length > 0 ? (
          userGridStates.map((gridState: any) => (
            <div
              key={uuidv4()}
              className="views-panel-grid-state-chip pulse-typography--body-2 pulse-mb-1"
            >
              <Chip
                label={gridState.display_name}
                variant="filled"
                onClick={() => loadGridState(gridState.grid_state)}
                onDelete={() =>
                  handleOpenDeleteViewModal(
                    gridState.grid_id,
                    gridState.display_name
                  )
                }
                deleteIcon={<DeleteIcon />}
              />
            </div>
          ))
        ) : (
          <p className="pulse-typography--body-1">No saved views</p>
        )}
      </div>

      <Dialog onClose={handleCloseDeleteViewModal} open={deleteViewModal.open}>
        <DialogTitle
          id="alert-dialog-title"
          sx={{ backgroundColor: "#e8eaef", color: "#091229" }}
        >
          {`Delete: ${deleteViewModal.display_name}`}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#e8eaef" }}>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "#091229" }}
          >
            Are you sure you want to delete this view? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#e8eaef" }}>
          <Button
            onClick={handleCloseDeleteViewModal}
            sx={{ color: "#091229" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              removeGridState(
                deleteViewModal?.grid_id || "",
                deleteViewModal?.display_name || ""
              )
            }
            sx={{ color: "#fe3c46", fontWeight: "bold" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
